import { Button, FormLabel, Grid, Input, Stack } from '@chakra-ui/react'
import type { AdditionSportEquipment, AdditionSportEquipmentTravel, OfferTravel, OfferTravelAddition, PackageItemModel } from 'BrokerTsLib/src/dtos'
import { Insurer } from 'BrokerTsLib/src/enums'
import type { UseFieldArrayRemove } from 'BrokerTsLib/src/rhf'
import { useFieldArray, useFormContext } from 'BrokerTsLib/src/rhf'
import { useEffect, useRef } from 'react'
import { Add } from '../../../../../icons'

type SportEquipmentsProps = {
  offer: OfferTravel
  addon: PackageItemModel
}

const getDefaultItem = (addon: PackageItemModel) => {
  const defaultVariant = addon.variants.find(x => x.isDefault) ?? addon.variants[0]

  return {
    riskId: addon.riskId,
    packageItemId: addon.packageItemId,
    variantId: defaultVariant?.id,
    limit: defaultVariant?.defaultLimit?.toString(),
  } as AdditionSportEquipment
}

function SportEquipments({
  offer,
  addon
}: SportEquipmentsProps) {

  const {
    control,
    setValue
  } = useFormContext<OfferTravelAddition>()

  const name = 'sportEquipments'

  const { append, fields, remove } = useFieldArray({
    control: control,
    name: name
  })

  // kvuli zachovani informace pri strictMode, kdy se kvuli mount a unmout vychozi prvek pridaval 2x
  const initializedRef = useRef(false)

  // vychozi nastaveni hodnot na OfferTravelAddition z OfferPart
  useEffect(() => {
    if (initializedRef.current) return

    fields.forEach((item, index) => {
      const part = offer.parts.find(x => x.partId == item.offerPartId)
      if (!part) return

      setValue(`${name}.${index}.riskId`, part.packageItem.riskId)
      setValue(`${name}.${index}.limit`, part.limit?.toString())
      setValue(`${name}.${index}.variantId`, part.packageItemVariantId)
      setValue(`${name}.${index}.offerPartId`, part.partId)
      setValue(`${name}.${index}.packageItemId`, part.packageItem.packageItemId)
    })

    if (fields.length == 0)
      append(getDefaultItem(addon) as AdditionSportEquipmentTravel)

    initializedRef.current = true
  }, [])

  return (
    <Grid gap={8} gridTemplateColumns='1fr 1fr 1fr'>
      {fields?.map((item, index) => {
        return <SportEquipmentItem
          key={`${item.offerPartId}_${index}`}
          index={index}
          item={item}
          remove={remove}
        />
      })}

      {offer.insurerId == Insurer.Direct &&
        <Stack>
          <Button
            variant='outline'
            onClick={() => append(getDefaultItem(addon) as AdditionSportEquipmentTravel)}
          >
            <Add color="secondary.500" />
            Další
          </Button>
        </Stack>
      }
    </Grid >
  )
}

export default SportEquipments

type SportEquipmentItemProps = {
  item: AdditionSportEquipment,
  index: number,
  remove: UseFieldArrayRemove
}

const SportEquipmentItem = ({
  item,
  index,
  remove
}: SportEquipmentItemProps) => {

  const name = 'sportEquipments'
  const { register } = useFormContext<OfferTravelAddition>()

  return (
    <Stack key={`${item.offerPartId}_${index}`} alignSelf='start' minW='120px'>
      <FormLabel alignSelf='center'>{index + 1}. ks vybavení</FormLabel>

      <Input
        size='sm'
        placeholder='Popis'
        {...register(`${name}.${index}.description`)} />

      <Button
        size='sm'
        colorScheme='secondary'
        variant='ghost'
        onClick={() => remove(index)}
      >
        Odebrat
      </Button>
    </Stack>
  )
}
